import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { SearchResult } from '@backrs_ui/common-ui';

import {
  SearchCategory,
  SearchPageCategory,
  SEARCH_PAGE_CATEGORIES,
} from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

export type PageSearchParams = Readonly<{
  searchTerm?: string;
  selectedFilters: SearchCategory[];
  searchResults?: SearchResult;
  hasInitialSearchBeenMade: boolean;
}>;

type SearchState = Readonly<{
  loading: boolean;
  error?: string;
  currentCategory: string;
  pageSearchParams: Record<SearchPageCategory, PageSearchParams>;
}>;

const initialState: SearchState = {
  loading: false,
  currentCategory: '',
  pageSearchParams: Object.values(SEARCH_PAGE_CATEGORIES).reduce(
    (acc, category) => ({
      ...acc,
      [category]: {
        searchTerm: '',
        selectedFilters: [],
        searchResults: undefined,
        hasInitialSearchBeenMade: false,
      },
    }),
    {} as Record<SearchPageCategory, PageSearchParams>,
  ),
};

export const searchForContentNuggets = createAsyncThunk(
  'search/searchForContentNuggets',
  async (
    params: {
      search_term: string;
      page?: number;
      perPage?: number;
      types?: SearchCategory[];
      category: SearchPageCategory;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = new URL(`${ENVIRONMENT_URL}/content-search`);

      const filter = JSON.stringify({
        q: params.search_term,
        ...((params.types?.length || 0) > 0 && { types: params.types }),
        exclude_theme: 'Grow by Doing',
      });

      const sort = JSON.stringify(['title', 'ASC']);

      url.searchParams.append('filter', filter);
      url.searchParams.append('sort', sort);

      if (params.page) {
        url.searchParams.append('page', params.page.toString());
      }
      if (params.perPage) {
        url.searchParams.append('perPage', params.perPage.toString());
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      return {
        result: data as SearchResult,
        category: params.category,
      };
    } catch (error) {
      return rejectWithValue(`Failed to fetch content nuggets with error: ${error}`);
    }
  },
);

export const searchSlice = createSlice({
  name: 'contentCompetency',
  initialState,
  reducers: {
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSearchParamsForCategory: (
      state,
      action: PayloadAction<{ category: SearchPageCategory; searchParams: PageSearchParams }>,
    ) => {
      state.pageSearchParams[action.payload.category] = {
        ...action.payload.searchParams,
        hasInitialSearchBeenMade: true,
      };
    },
    setSearchTermForCategory: (
      state,
      action: PayloadAction<{ category: SearchPageCategory; searchTerm: string }>,
    ) => {
      state.pageSearchParams[action.payload.category].searchTerm = action.payload.searchTerm;
      state.pageSearchParams[action.payload.category].hasInitialSearchBeenMade = false;
    },
    resetSearchParamsForCategory: (state, action: PayloadAction<SearchPageCategory>) => {
      state.pageSearchParams[action.payload] = {
        searchTerm: '',
        selectedFilters: [],
        searchResults: undefined,
        hasInitialSearchBeenMade: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchForContentNuggets.pending, (state, action) => {
        state.loading = true;
        state.error = undefined;

        const category = action.meta.arg.category;

        if (state.pageSearchParams[category]?.searchResults) {
          const currentParams = state.pageSearchParams[category];
          const currentResults = currentParams.searchResults;

          state.pageSearchParams[category] = {
            ...currentParams,
            hasInitialSearchBeenMade: true,
            searchResults: {
              results: [],
              page: currentResults?.page || 1,
              perPage: currentResults?.perPage || 6,
              total: currentResults?.total || 0,
            },
          };
        }
      })
      .addCase(
        searchForContentNuggets.fulfilled,
        (state, action: PayloadAction<{ category: SearchPageCategory; result: SearchResult }>) => {
          state.pageSearchParams[action.payload.category].searchResults = action.payload.result;
          state.pageSearchParams[action.payload.category].hasInitialSearchBeenMade = true;
          state.loading = false;
        },
      )
      .addCase(searchForContentNuggets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // LOGOUT
      .addCase(logout, () => {
        return { ...initialState };
      });
  },
});

export const selectSearchResults = (state: RootState, category: SearchPageCategory) =>
  state.search.pageSearchParams[category].searchResults;

export const selectSearchTerm = (state: RootState, category: SearchPageCategory) =>
  state.search.pageSearchParams[category].searchTerm;

export const {
  setSearchLoading,
  setSearchParamsForCategory,
  resetSearchParamsForCategory,
  setSearchTermForCategory,
} = searchSlice.actions;
